import React, { useState } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames/bind'

import { RichText, linkResolver } from 'prismic'
import { Row, Column12 } from 'components/ui/Grid'
import { Section, Container } from 'components/layout'
import SplitText from 'components/motion/SplitText'
import ViewportEnter from 'components/motion/ViewportEnter'
import { Headline2, Subtitle, BodyCopy } from 'components/ui/Typography'
import Button from 'components/ui/Button'
import Clickable from 'components/ui/Clickable'
import ThemeWaypoint from 'components/ui/ThemeWaypoint'
import { TransitionState } from 'gatsby-plugin-transition-link'

import s from './InfoSection.module.scss'
const cn = classNames.bind(s)

const getTitleLinkParts = (title, linkText) => {
  const trimmedLinkText = linkText?.trim()
  if (!title || !trimmedLinkText) return null

  const index = title.toLowerCase().indexOf(trimmedLinkText.toLowerCase())
  if (index === -1) return null

  return {
    before: title.slice(0, index),
    match: title.slice(index, index + trimmedLinkText.length),
    after: title.slice(index + trimmedLinkText.length),
  }
}

const InfoSection = ({ data, hideOnMobile = false, waypoint, contentFullWidth = false, boldTitle = false }) => {
  const [isSectionInView, setIsSectionInView] = useState(false)
  const [isInView, setIsInView] = useState(false)
  const [isContentInView, setIsContentInView] = useState(false)
  if (!data?.primary) return null
  const { title, subtitle, content, content_rich, link, link_text, content_full_width } = data?.primary || {}
  const titleText = Array.isArray(title) ? title?.[0]?.text : title
  const subtitleText = Array.isArray(subtitle) ? subtitle?.[0]?.text : subtitle
  const contentData = Array.isArray(content_rich) && content_rich.length ? content_rich : content
  const isRichTextContent = Array.isArray(contentData)
  const hasContent = isRichTextContent ? contentData.length > 0 : Boolean(contentData)
  const renderedContent = isRichTextContent ? RichText.render(contentData) : contentData
  const resolvedDesktopColumns = contentFullWidth || content_full_width ? 12 : 6
  // Internal doc links from PrismicDocLinkFrag only carry _meta (no _linkType),
  // so Link.url() can't resolve them — use linkResolver directly instead.
  const linkUrl = link?._meta ? linkResolver(link._meta) : link?.url || null
  const titleLinkParts = linkUrl ? getTitleLinkParts(titleText, link_text) : null
  const isTitleLink = Boolean(titleLinkParts)

  return (
    <TransitionState>
      {({ mount, transitionStatus, current } = {}) => (
        <div className={cn({ isSectionInView, [transitionStatus]: transitionStatus })}>
          {waypoint && <ThemeWaypoint after={waypoint} className={cn('waypoint')} />}
          <Section
            hideOnMobile={hideOnMobile}
            onEnter={() => setIsSectionInView(true)}
            onExit={() => setIsSectionInView(false)}
            once={false}
          >
            <Container>
              <Row>
                <Column12 initial={10} medium={8} large={6} center>
                  <ViewportEnter onEnter={() => setIsInView(true)}>
                    <div className={cn('wrapper', { inView: isInView })}>
                      {subtitleText && (
                        <div className={cn('subtitle')}>
                          <Subtitle vertical className={isInView ? 'animate' : ''}>
                            {subtitleText}
                          </Subtitle>
                        </div>
                      )}
                      {titleText && (
                        <div className={cn('headline')}>
                          <Headline2 style={boldTitle ? { fontWeight: 700 } : undefined}>
                            {isTitleLink ? (
                              <>
                                {titleLinkParts.before}
                                <Clickable to={linkUrl} className={cn('titleLink')}>
                                  {titleLinkParts.match}
                                </Clickable>
                                {titleLinkParts.after}
                              </>
                            ) : (
                              <SplitText>{titleText}</SplitText>
                            )}
                          </Headline2>
                        </div>
                      )}
                      {hasContent && (
                        <ViewportEnter onEnter={() => setIsContentInView(true)}>
                          <div className={cn('content', { animate: isContentInView })}>
                            <Row>
                              <Column12 desktop={resolvedDesktopColumns} right>
                                <BodyCopy as="div" className={cn('copy')}>
                                  {renderedContent}
                                </BodyCopy>
                              </Column12>
                            </Row>
                          </div>
                        </ViewportEnter>
                      )}
                      {linkUrl && !isTitleLink && (
                        <div className={cn('btn')}>
                          <Button to={linkUrl} isBold>
                            {link_text}
                          </Button>
                        </div>
                      )}
                    </div>
                  </ViewportEnter>
                </Column12>
              </Row>
            </Container>
          </Section>
        </div>
      )}
    </TransitionState>
  )
}

InfoSection.propTypes = {
  data: PropTypes.object,
  hideOnMobile: PropTypes.bool,
  waypoint: PropTypes.oneOf(['light', 'dark']),
  contentFullWidth: PropTypes.bool,
  boldTitle: PropTypes.bool,
}

export default InfoSection
